import {css} from '@emotion/core';
import {Icon} from 'antd';
import {IconProps} from 'antd/lib/icon';
import moment, {Moment} from 'moment';
import {darken, readableColor} from 'polished';
import React, {
  ComponentProps,
  ComponentType,
  Dispatch,
  FC,
  forwardRef,
  ForwardRefExoticComponent,
  memo,
  PropsWithoutRef,
  ReactElement,
  ReactNode,
  RefAttributes,
  RefForwardingComponent,
  SetStateAction,
} from 'react';
import {
  Group,
  SELECTED_EARS,
  SELECTED_EARS_TEXT,
} from '../components/Options/static';
import {Option, TBoolean, Tuple} from '../contexts/Quote';

export const colors = {
  primary: '#006fd0',
  secondary: '#769e1d',
};

export const cssUtils = {
  height: (em: number) =>
    css`
      height: ${em}em;
    `,
  heightP: (percent = 100) =>
    css`
      height: ${percent}%;
    `,
  vHidden: css`
    visibility: hidden;
  `,
};

export const cssDisabledLine = css`
  background-color: lightgray;
  color: grey;
  cursor: not-allowed;
`;

export const cssRowWithoutOuterGutter = css`
  > .ant-col:first-of-type {
    padding-left: 0 !important;
  }
  > .ant-col:last-of-type {
    padding-right: 0 !important;
  }
`;

export const secondaryButtonCss = css`
  --secondary-color: ${colors.secondary};
  --hover-secondary-color: ${darken(0.1, colors.secondary)};
  --border-secondary-color: ${darken(0.3, colors.secondary)};

  background-color: var(--secondary-color);
  color: ${readableColor(colors.secondary)};

  &:hover {
    background-color: var(--hover-secondary-color);
    border-color: var(--border-secondary-color);
    color: ${readableColor(colors.secondary)};
  }
`;

export const renderIconInputPrefix = (
  icon: string,
  props: IconProps = {},
): ReactElement => <Icon type={icon} style={{color: '#bfbfbf'}} {...props} />;

export const cssCursorPointer = css`
  cursor: pointer;
`;

export const cssOption = css`
  padding: 5px 0;
  text-align: center;
  border: 1px solid black;
  @media (min-width: 992px) {
    padding: 10px 16px;
    font-size: 20px;
  }
`;

export const borderContainerCss = css`
  .ant-row:not(:last-of-type) .ant-col,
  .ant-row-flex:not(:last-of-type) .ant-col {
    border-bottom-width: 0;
  }

  .ant-row .ant-col:not(:last-of-type),
  .ant-row-flex .ant-col:not(:last-of-type) {
    border-right-width: 0;
  }
`;

const {format: _currencyFormat} = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
});

const {format: _dateFormat} = new Intl.DateTimeFormat('fr-FR', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
});

export const currencyFormat = (nb: number, plain = false): string => {
  if (plain) {
    return _currencyFormat(nb);
  }

  if (nb >= 1000000000) {
    return `MM${_currencyFormat(nb / 1000000000)}`;
  }
  if (nb >= 1000000) {
    return `M${_currencyFormat(nb / 1000000)}`;
  }
  if (nb >= 1000) {
    return `k${_currencyFormat(nb / 1000)}`;
  }

  return _currencyFormat(nb);
};

export const Currency: FC<{price: number}> = ({price}) => (
  <span>{price ? currencyFormat(price, true) : '-- €'}</span>
);

export const Date: FC<{timestamp: number}> = ({timestamp}) => (
  <span>{_dateFormat(timestamp)}</span>
);

export const BuhBye: FC = () => (
  <div css={{fontSize: 20}}>
    <span role="img" aria-labelledby="bye">
      👋
    </span>
  </div>
);

export const cssRightEar = css`
  display: inline-block;
  transform: rotateY(180deg);
`;

export const SELECTED_EARS_EMOJIS: {[selected in SELECTED_EARS]: ReactNode} = {
  [SELECTED_EARS.NONE]: null,
  [SELECTED_EARS.LEFT_ONLY]: (
    <span
      role="img"
      aria-labelledby={SELECTED_EARS_TEXT[SELECTED_EARS.LEFT_ONLY]}
    >
      👂&nbsp;❌
    </span>
  ),
  [SELECTED_EARS.RIGHT_ONLY]: (
    <span
      role="img"
      css={cssRightEar}
      aria-labelledby={SELECTED_EARS_TEXT[SELECTED_EARS.LEFT_ONLY]}
    >
      👂&nbsp;❌
    </span>
  ),
  [SELECTED_EARS.BOTH]: (
    <span role="img" aria-labelledby={SELECTED_EARS_TEXT[SELECTED_EARS.BOTH]}>
      {/* eslint-disable-next-line jsx-a11y/accessible-emoji */}
      👂&nbsp;<span css={cssRightEar}>👂</span>
    </span>
  ),
};

export const DisabledEar: FC = () => (
  <span role="img" aria-labelledby="Oreille désactivée" />
);

// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const typedMemo: <T extends ComponentType<any>>(
  Component: T,
  propsAreEqual?: (
    prevProps: Readonly<ComponentProps<T>>,
    nextProps: Readonly<ComponentProps<T>>,
  ) => boolean,
) => T = memo;
export const typedForwardRef: <T, P = {}>(
  Component: RefForwardingComponent<T, P>,
) => ForwardRefExoticComponent<
  PropsWithoutRef<P> & RefAttributes<T>
> = forwardRef;

export const fixTimestampMilliseconds = (timestamp: number): number =>
  timestamp < 9999999999 ? timestamp * 1000 : timestamp;

export const getMomentFromTimestamp = (timestamp: number): Moment =>
  moment.utc(fixTimestampMilliseconds(timestamp));

export function renderTimestamp(timestamp: number): ReactNode {
  return timestamp ? (
    <Date timestamp={fixTimestampMilliseconds(timestamp)} />
  ) : null;
}

export const getSelectedGroups = (
  selectedOptions: Option[],
): {[key: string]: TBoolean} =>
  selectedOptions.reduce((_res, {type, selected}) => {
    if (!_res[type]) {
      _res[type] = [false, false];
    }

    selected.map(
      (_selected, index) =>
        (_res[type][index] = _res[type][index] || _selected),
    );

    return _res;
  }, {} as {[key: string]: TBoolean});

const getRequiredGroups = (groups: Group[]): Group[] =>
  groups.filter(({required}) => required);

export const getEmptyRequiredGroups = (
  selectedOptions: Option[],
  groups: Group[],
): Group[] => {
  const selectedGroups = getSelectedGroups(selectedOptions);
  return getRequiredGroups(groups).filter(({name}) => !selectedGroups[name]);
};

export const getMissingEarsRequiredGroups = ({
  ears,
  groups,
  selectedOptions,
}: {
  ears: TBoolean;
  groups: Group[];
  selectedOptions: Option[];
}): Tuple<Group[]> => {
  const requiredGroups = getRequiredGroups(groups);
  const selectedGroups = getSelectedGroups(selectedOptions);
  const emptyRequiredGroups = getEmptyRequiredGroups(selectedOptions, groups);

  return ears.map((enabled, ear) =>
    enabled
      ? requiredGroups.filter(
          (group) =>
            (!selectedGroups[group.name] || !selectedGroups[group.name][ear]) &&
            !emptyRequiredGroups.includes(group),
        )
      : [],
  ) as Tuple<Group[]>;
};

export const getMissingGroups = (
  ears: Tuple<boolean>,
  selectedOptions: Option[],
  groups: Group[],
): {
  emptyRequiredGroups: Group[];
  missingEarsRequiredGroups: Tuple<Group[]>;
  missingGroups: Group[];
} => {
  const emptyRequiredGroups = getEmptyRequiredGroups(selectedOptions, groups);
  const missingEarsRequiredGroups = getMissingEarsRequiredGroups({
    ears,
    groups,
    selectedOptions,
  });

  const missingGroups = [
    ...emptyRequiredGroups,
    ...missingEarsRequiredGroups.flat(),
  ];
  return {emptyRequiredGroups, missingEarsRequiredGroups, missingGroups};
};

export type StateOf<T> = [T, Dispatch<SetStateAction<T>>];

export type Dispatcher<T> = (value: T) => void;
export type ValueDispatcher<T> = [T, Dispatcher<T>];
export type ValuePartialDispatcher<T> = [T, Dispatcher<Partial<T>>];
export type NullableValueDispatcher<T> = [T | null, Dispatcher<T | undefined>];
